import React from 'react'
import type { ButtonHTMLAttributes, ElementType } from 'react'
import { useIsMobile } from '@/shared/hooks/use-is-mobile'

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  as?: ElementType
  variant?: ButtonVariant
  size?: ButtonSize
  sizeMobile?: ButtonSize
  loading?: boolean
  leadingIcon?: React.ElementType
  trailingIcon?: React.ElementType
  fullWidth?: boolean
}

const BASE =
  'inline-flex cursor-pointer select-none place-items-center justify-center gap-1.5 rounded-full font-sans leading-none focus:outline-none transition'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-indigo-base text-white ' +
    'hover:bg-indigo-600 hover:not-active:shadow-btn-hover ' +
    'active:bg-indigo-600 ' +
    'focus-visible:ring-2 focus-visible:ring-indigo-800 focus-visible:border-transparent',
  secondary:
    'border border-indigo-base bg-transparent text-indigo-base ' +
    'hover:not-active:bg-indigo-50 hover:not-active:shadow-btn-hover ' +
    'active:bg-indigo-100 active:border-indigo-100 ' +
    'focus-visible:text-indigo-800 focus-visible:ring-2 focus-visible:ring-indigo-800 focus-visible:border-transparent',
  tertiary:
    'bg-transparent text-indigo-base ' +
    'hover:not-active:bg-indigo-50 ' +
    'active:bg-indigo-100 ' +
    'focus-visible:text-indigo-800 focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-800',
  link:
    'h-auto bg-transparent p-0 leading-4 text-indigo-base underline-offset-2 ' +
    'focus-visible:text-indigo-800',
}

const DISABLED_STYLES: Record<ButtonVariant, string> = {
  primary:
    'disabled:pointer-events-none disabled:bg-neutral-50 disabled:border disabled:border-neutral-300 disabled:text-neutral-300 disabled:shadow-none',
  secondary:
    'disabled:pointer-events-none disabled:border-neutral-300 disabled:bg-neutral-50 disabled:text-neutral-300',
  tertiary: 'disabled:pointer-events-none disabled:text-neutral-300',
  link: 'disabled:pointer-events-none disabled:text-neutral-300',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'py-2 px-4 text-sm',
  md: 'py-3 px-8 text-base tracking-[0.16px]',
  lg: 'py-4 px-10 text-base tracking-[0.16px]',
}

const LINK_SIZES: Record<ButtonSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
}

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

export function Button({
  as: ButtonTag = 'button',
  variant = 'primary',
  size = 'md',
  sizeMobile,
  loading = false,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  fullWidth = false,
  className = '',
  disabled,
  children,
  ...props
}: ButtonProps) {
  const isMobile = useIsMobile()
  const resolvedSize = sizeMobile && isMobile ? sizeMobile : size
  const classes = [
    BASE,
    VARIANTS[variant],
    variant === 'link' ? LINK_SIZES[resolvedSize] : SIZES[resolvedSize],
    disabled && !loading ? DISABLED_STYLES[variant] : '',
    fullWidth ? 'w-full' : '',
    loading ? 'cursor-wait pointer-events-none' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <ButtonTag className={classes} disabled={disabled || loading} {...props}>
      {loading ? (
        <span className="loading loading-spinner loading-xs" />
      ) : LeadingIcon ? (
        <LeadingIcon className={ICON_SIZES[resolvedSize]} />
      ) : (
        <></>
      )}
      {children}
      {!loading && TrailingIcon && (
        <TrailingIcon className={ICON_SIZES[resolvedSize]} />
      )}
    </ButtonTag>
  )
}
